// Whether the user has asked for less motion. The unlock celebration's
// confetti (GLOSSARY.md) and the refresh reveal's card flip, breath, and
// sparkle all stand down on it, so the question has one owner rather than a
// media query copied per feature.
export function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

// Whether element may animate on the Web Animations API. Reduced motion, and
// an environment without the API (an old WebView, happy-dom), get no motion:
// the refresh reveal swaps its faces instead (GLOSSARY.md "Refresh reveal"),
// the starter pack shimmer stays invisible.
export function canAnimate(element: Element): boolean {
  if (prefersReducedMotion()) return false
  return typeof element.animate === 'function'
}

// One animated property's timeline, the way motion data reads: the values it
// passes through, when, and how it gets from each to the next. Tracks animate
// the independent CSS transform properties (`translate`/`scale`/`rotate`)
// rather than `transform`, so each keeps its own timing and easing on a
// single element and composes with a transform the element already holds.
export interface MotionTrack {
  property: 'opacity' | 'rotate' | 'scale' | 'translate'
  /** One keyframe value per entry in `times`. */
  values: (number | string)[]
  /** Timeline offsets (0–1). */
  times: number[]
  /** Per-segment easing; a single string covers every segment. */
  easing: string | string[]
}

/** One track as a WAAPI keyframe list, ready for `element.animate`. */
export function trackKeyframes(track: MotionTrack): Keyframe[] {
  return track.values.map((value, index) => {
    const keyframe: Keyframe = {
      offset: track.times[index],
      [track.property]: value,
    }
    if (index < track.values.length - 1) {
      keyframe.easing =
        typeof track.easing === 'string' ? track.easing : track.easing[index]
    }
    return keyframe
  })
}
